import React, { ReactElement } from 'react'

import { useAppSelector } from '../../../store/hooks'
import CustomerDataSection from '../../molecules/CustomerDataSection'

const Component = (): ReactElement => {
  const customerInputValues = useAppSelector(state => state.customervalues)

  function extractValue (input: any): string {
    return typeof input === 'object' ? input.label : input
  }

  const geburtstag = new Date(customerInputValues['p-customer']['customer-geburtstag'])
  const formattedGeburtstag = geburtstag.toLocaleDateString(
    'de-DE',
    { day: '2-digit', month: '2-digit', year: 'numeric' }
  )

  const inbetriebnahme = new Date(customerInputValues['p-generator']['generator-anlage-inbetriebnahme'])
  const inbetriebnahmeFormatted = inbetriebnahme.toLocaleDateString(
    'de-DE',
    { day: '2-digit', month: '2-digit', year: 'numeric' }
  )

  const cloudToGoSelected = customerInputValues['p-intro']['intro-ctg-paketCloudToGo']
  const heatSelected = customerInputValues['p-intro']['intro-wae-waermePaket']
  const fafSelected = customerInputValues['p-intro']['intro-faf-paketFamilyAndFriends']
  const faf2Selected = customerInputValues['p-intro']['intro-faf-2-details']

  const anyAdditionalProductSelected = cloudToGoSelected || heatSelected || fafSelected

  function getChangeReasonText (reason: string): string {
    const reasonSplit = reason.split('-')
    const actualReason = reasonSplit[reasonSplit.length - 1]

    if (actualReason === 'lieferantwechsel') {
      return 'Ich möchte den Lieferanten wechseln'
    } else if (actualReason === 'neuEingezogen') {
      return 'Ich bin umgezogen bzw. neu eingezogen.'
    } else {
      return actualReason
    }
  }

  function getPaymentMethodText (method: string): string {
    if (method === 'customer-zahlungsweise-ueberweisung') { return 'Überweisung' } else { return 'SEPA Lastschriftmandat erteilt' }
  }

  function getBillingAddress (): string[] {
    const customerZipValue = extractValue(customerInputValues['p-customer']['customer-postleitzahl-abw'])
    if (customerZipValue) {
      const customerOrtValue = extractValue(customerInputValues['p-customer']['customer-ort-abw'])
      return [
        extractValue(customerInputValues['p-customer']['customer-strasse-abw']) + ' ' +
        customerInputValues['p-customer']['customer-hausnummer-abw'] + ' \n',
        String(customerZipValue.padStart(5, '0')) + ' ' +
        customerOrtValue
      ]
    } else {
      return getDeliveryAddress()
    }
  }

  function getGeneratorAddress (): string[] {
    const generatorZipValue = extractValue(customerInputValues['p-generator']['generator-anlagenstandort-postleitzahl'])
    if (generatorZipValue) {
      const generatorOrtValue = extractValue(customerInputValues['p-generator']['generator-anlagenstandort-ort'])
      return [
        extractValue(customerInputValues['p-generator']['generator-anlagenstandort-strasse']) + ' ' +
        customerInputValues['p-generator']['generator-anlagenstandort-hausnummer'] + ' \n',
        String(generatorZipValue.padStart(5, '0')) + ' ' +
        generatorOrtValue
      ]
    } else {
      return getDeliveryAddress()
    }
  }

  function getDeliveryAddress (): string[] {
    const deliveryZip = extractValue(customerInputValues['p-customer']['customer-postleitzahl'])

    if (deliveryZip) {
      const deliveryCity = extractValue(customerInputValues['p-customer']['customer-ort'])
      const deliveryStreet = extractValue(customerInputValues['p-customer']['customer-strasse'])
      return [
        deliveryStreet + ' ' +
        customerInputValues['p-customer']['customer-hausnummer'] + ' \n',
        String(deliveryZip.padStart(5, '0')) + ' ' +
        deliveryCity
      ]
    } else {
      return ['wie Lieferanschrift']
    }
  }

  function getSecondGeneratorOwner (): string[] {
    if (customerInputValues['p-generator']['generator-weitere-anlagenbetreiber-vorname2']) {
      return [
        customerInputValues['p-generator']['generator-weitere-anlagenbetreiber-vorname2'] + ' ' +
        customerInputValues['p-generator']['generator-weitere-anlagenbetreiber-name2']
      ]
    } else {
      return []
    }
  }

  const sectionsBase = [
    {
      section: 'Ihre persönlichen Daten',
      headlines: {
        Vertragsinhaber: [
          customerInputValues['p-customer']['customer-titel'] + ' ' +
          customerInputValues['p-customer']['customer-vorname'] + ' ' +
          customerInputValues['p-customer']['customer-nachname'],
          formattedGeburtstag
        ],
        Kontakt: [
          customerInputValues['p-customer']['customer-emailAddresse'],
          ...(customerInputValues['p-customer']['customer-telefonnummer']
            ? [customerInputValues['p-customer']['customer-telefonnummer']]
            : [])
        ]
      }
    },
    {
      section: 'Ihre Rechnung & Lieferung',
      headlines: {
        Lieferanschrift: getDeliveryAddress(),
        Wechselgrund: [
          getChangeReasonText(customerInputValues['p-vendor']['vendor-waerme-wechselgrund'])
        ],
        ...(customerInputValues['p-vendor']['vendor-waerme-zaehlernummer']
          ? {
              Zählernummer: [customerInputValues['p-vendor']['vendor-waerme-zaehlernummer']]
            }
          : {}),
        ...(customerInputValues['p-vendor']['marktlokations-identifikationsnummer']
          ? {
              'Marktlokations-ID': [customerInputValues['p-vendor']['marktlokations-identifikationsnummer']]
            }
          : {}),
        Rechnungsadresse: getBillingAddress(),
        Zahlungsart: [
          getPaymentMethodText(customerInputValues['p-customer']['customer-zahlungsweise'])
        ]
      }
    },
    {
      section: 'Ihre Daten zur PV-Anlage',
      headlines: {
        Inbetriebnahmedatum: [
          inbetriebnahmeFormatted
        ],
        Anlagenstandort: getGeneratorAddress(),
        Anlagenbetreiber: [
          customerInputValues['p-generator']['generator-weitere-anlagenbetreiber-vorname'] + ' ' +
          customerInputValues['p-generator']['generator-weitere-anlagenbetreiber-name']
        ],
        ...(getSecondGeneratorOwner().length > 0 ? { 'Weiterer Anlagenbetreiber': getSecondGeneratorOwner() } : {}),
        'Leistung der PV-Anlage': [
          customerInputValues['p-intro']['pv-nominal-power-kwp'] + ' kWp'
        ]
      }
    }
  ]

  const sectionsAdditional = []
  if (cloudToGoSelected) {
    sectionsAdditional.push({
      section: 'SENEC.Cloud to Go',
      headlines: {
        'Jährliche Fahrleistung und Verbrauch': [
          customerInputValues['p-intro']['intro-ctg-annual-mileage'] + ' km',
          customerInputValues['p-intro']['intro-ctg-car-consumption'] + ' kWh/ 100km'
        ],
        'Anteil zu Hause geladen': [
          customerInputValues['p-intro']['intro-ctg-at-home'] + '%'
        ]
      }
    })
  }

  if (fafSelected) {
    const faf1OrtValue = extractValue(customerInputValues['p-vendor']['vendor-faf-1-ort'])
    const faf1StrasseValue = extractValue(customerInputValues['p-vendor']['vendor-faf-1-strasse'])

    sectionsAdditional.push({
      section: 'SENEC.Family & Friends',
      headlines: {
        'Lieferanschrift 1. Abnahmestelle': [
          faf1StrasseValue + ' ' +
          customerInputValues['p-vendor']['vendor-faf-1-hausnummer'] + ' \n',
          customerInputValues['p-vendor']['vendor-faf-1-postleitzahl'] + ' ' +
          faf1OrtValue
        ],
        Anschlussnutzer: [
          customerInputValues['p-vendor']['vendor-faf-1-vorname'] + ' ' +
          customerInputValues['p-vendor']['vendor-faf-1-name'] + ' '
        ],
        Zählernummer: [
          customerInputValues['p-vendor']['vendor-faf-1-zaehlernummer']
        ],
        Wechselgrund: [
          getChangeReasonText(customerInputValues['p-vendor']['vendor-faf-1-wechselgrund'])
        ]
      }
    })

    if (faf2Selected && customerInputValues['p-vendor']['vendor-faf-2-ort']) {
      const faf2OrtValue = extractValue(customerInputValues['p-vendor']['vendor-faf-2-ort'])
      const faf2StrasseValue = extractValue(customerInputValues['p-vendor']['vendor-faf-2-strasse'])

      sectionsAdditional.push({
        section: 'SENEC.Family & Friends 2',
        headlines: {
          'Lieferanschrift 2. Abnahmestelle': [
            faf2StrasseValue + ' ' +
            customerInputValues['p-vendor']['vendor-faf-2-hausnummer'] + ' \n',
            customerInputValues['p-vendor']['vendor-faf-2-postleitzahl'] + ' ' +
            faf2OrtValue
          ],
          Anschlussnutzer: [
            customerInputValues['p-vendor']['vendor-faf-2-vorname'] + ' ' +
            customerInputValues['p-vendor']['vendor-faf-2-name'] + ' '
          ],
          Zählernummer: [
            customerInputValues['p-vendor']['vendor-faf-1-zaehlernummer']
          ],
          Wechselgrund: [
            getChangeReasonText(customerInputValues['p-vendor']['vendor-faf-2-wechselgrund'])
          ]
        }
      })
    }
  }

  if (heatSelected) {
    sectionsAdditional.push({
      section: 'SENEC.Wärme',
      headlines: {
        Zählernummer: [
          customerInputValues['p-vendor']['vendor-waerme-zaehlernummer']
        ],
        Wechselgrund: [
          getChangeReasonText(customerInputValues['p-vendor']['vendor-wae-wechselgrund'])
        ]
      }
    })
  }

  return (
    <div className="md:m-5">
      {sectionsBase.map((section, index) => (
        <CustomerDataSection section={section} index={index} />
      ))}
      {anyAdditionalProductSelected && (
        <div className="text-center md:p-10 md:py-10">
          <h2 className="h2">Ihre Daten zu Zusatzprodukten</h2>
        </div>
      )}
      {anyAdditionalProductSelected && sectionsAdditional.map((section, index) => (
        <CustomerDataSection section={section} index={index} />
      ))}
    </div>
  )
}

export default Component
